import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import type { RedlinePayload } from "@common/utils/redline/wire/redline-payload";

import { anchorTrackers, documentPages } from "../utils/anchor/anchor-tracker-registry";
import { AnchorLog } from "../utils/anchor/anchor-log";
import { LIVE_PROPOSAL_OFF, type LiveProposalState, type ProposalEditLayerInput } from "../utils/anchor/proposal/live-proposal";
import { type LiveProposalHold, type LiveProposalLease, LiveProposalRegistry } from "../utils/anchor/proposal/live-proposal-registry";

export const liveProposals = new LiveProposalRegistry({
    trackerFor: (bookmarkNames, redline) => anchorTrackers.trackerFor(bookmarkNames, redline),
    get word() {
        return anchorTrackers.word;
    },
    get pages() {
        return documentPages;
    },
});

export const LiveProposalRegistryContext = createContext<LiveProposalRegistry>(liveProposals);

export function useLiveProposalRegistry(): LiveProposalRegistry {
    return useContext(LiveProposalRegistryContext);
}

export interface LiveProposalHandle extends LiveProposalState {
    refind: () => Promise<void>;
}

interface UseLiveProposalArgs {
    bookmarkNames: string[];
    /** The card's redline in either shape, or null while the card cannot be previewed yet. */
    redline: RedlinePayload | null;
    editLayer: ProposalEditLayerInput | null;
    enabled: boolean;
    hold: LiveProposalHold;
}

export function useLiveProposal({ bookmarkNames, redline, editLayer, enabled, hold }: UseLiveProposalArgs): LiveProposalHandle {
    const registry = useLiveProposalRegistry();
    const [state, setState] = useState<LiveProposalState>(LIVE_PROPOSAL_OFF);
    const leaseRef = useRef<LiveProposalLease | null>(null);
    const appliedEditLayerKeyRef = useRef<string | null>(null);
    const bookmarkKey = bookmarkNames.join("|");
    const redlineKey = redline === null ? null : JSON.stringify(redline);
    const editLayerKey = editLayer === null ? null : JSON.stringify(editLayer);

    useEffect(() => {
        const liveProposalCanRun = enabled && redline !== null && bookmarkNames.length > 0;
        if (!liveProposalCanRun) {
            setState(LIVE_PROPOSAL_OFF);
            leaseRef.current = null;
            return;
        }
        let lease: LiveProposalLease;
        try {
            lease = registry.acquire({ bookmarkNames, redline, editLayer }, hold);
        } catch (error) {
            setState({ ...LIVE_PROPOSAL_OFF, status: "error", error: AnchorLog.messageOf(error) });
            leaseRef.current = null;
            return;
        }
        leaseRef.current = lease;
        appliedEditLayerKeyRef.current = editLayerKey;
        const unsubscribe = lease.proposal.subscribe(setState);
        return () => {
            unsubscribe();
            lease.release();
            if (leaseRef.current === lease) leaseRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registry, enabled, bookmarkKey, redlineKey, hold]);

    useEffect(() => {
        if (appliedEditLayerKeyRef.current === editLayerKey) return;
        appliedEditLayerKeyRef.current = editLayerKey;
        leaseRef.current?.setEditLayer(editLayer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editLayerKey]);

    const refind = useCallback((): Promise<void> => leaseRef.current?.proposal.refind() ?? Promise.resolve(), []);

    return { ...state, refind };
}
